import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {InteractionAreaData} from "../../../types/InteractionArea";
import InteractionAreaForm from "../Form";

function MutateInteractionArea() {
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: interactionArea, isLoading} = useQuery(
    ['interaction-areas', {id}],
    () => ngage.interactionAreas.get(Number(id)),
    {enabled: !!id && auth.can('interaction-areas:read')}
  )

  const store = useMutation((data: InteractionAreaData) => ngage.interactionAreas.store(data))
  const update = useMutation((data: InteractionAreaData) => ngage.interactionAreas.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: InteractionAreaData, {setSubmitting}: FormikHelpers<InteractionAreaData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('interaction-areas.all')

    setSubmitting(false)
    navigate('/interaction-areas')
  }

  // Handle store mutation
  async function handleStore(values: InteractionAreaData) {
    try {
      await store.mutateAsync(values)
      notify("success", `Interaction Area Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `Interaction Area Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: InteractionAreaData) {
    try {
      await update.mutateAsync(values)
      notify("success", `Interaction Area Updated`, `${values.name} has been updated`)
      await invalidate(['interaction-areas', {id}])
    } catch (error) {
      notify("error", `Interaction Area Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised mutation
  if (auth.cannot('interaction-areas:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'Operations': '/',
    'Interaction Areas': '/interaction-areas',
    'New Interaction Area': '/interaction-areas/new'
  }

  const editBreadcrumbs = {
    'Operations': '/',
    'Interaction Areas': '/interaction-areas',
    'Edit Interaction Area': `/interaction-areas/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} Interaction Area`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <InteractionAreaForm interactionArea={interactionArea} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateInteractionArea;